import { useState, type FormEvent } from 'react';
import { Carte, MessageErreur, Pastille } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { dateReunion } from '../../lib/useFormateurs';
import {
  useAppliquerDomaine,
  useRetirerDomaine,
  useVerifierDomaine,
  type EtatDomaine,
  type VueDomaine,
} from '../../lib/useParametrage';
import { CLASSE_BOUTON_ENREGISTRER, messageErreur } from './commun';

const TONS_ETAT: Record<EtatDomaine, string> = {
  RACCORDE: 'success',
  INTROUVABLE: 'danger',
  AUTRE_SERVEUR: 'danger',
  HTTPS_INDISPONIBLE: 'warning',
  AUTRE_PLATEFORME: 'danger',
};

const CLASSE_BOUTON_SECONDAIRE =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50';

/**
 * Domaine personnalisé (TF-436) : l'adresse des liens que la plateforme envoie
 * — e-mails, SMS, lien de préinscription. Il ne se branche pas d'ici : le DNS
 * se règle chez le registraire du domaine, le serveur web et son certificat
 * chez Codialis. L'écran vérifie le raccordement, puis l'applique.
 */
export function SectionDomaine({ domaine }: { domaine: VueDomaine }): JSX.Element {
  const toast = useToast();
  const verifier = useVerifierDomaine();
  const appliquer = useAppliquerDomaine();
  const retirer = useRetirerDomaine();
  const [saisie, setSaisie] = useState(domaine.verification?.domaine ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmerRetrait, setConfirmerRetrait] = useState(false);
  const enCours = verifier.isPending || appliquer.isPending || retirer.isPending;

  const lancerVerification = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    verifier.mutate(saisie, {
      // La saisie prend la forme lue par l'API : « https://X.re/ » devient « x.re ».
      onSuccess: (vue) => setSaisie(vue.verification?.domaine ?? saisie),
      onError: (refus) => setErreur(messageErreur(refus, 'Le domaine n’a pas pu être vérifié.')),
    });
  };

  const lancerApplication = (): void => {
    setErreur(null);
    appliquer.mutate(saisie, {
      onSuccess: (vue) => toast(`Les liens envoyés partent désormais de ${vue.adresseActuelle}`),
      onError: (refus) => setErreur(messageErreur(refus, 'Le domaine n’a pas pu être appliqué.')),
    });
  };

  const verification = domaine.verification;
  const dejaApplique = domaine.applique?.domaine === verification?.domaine;

  return (
    <Carte titre="Nom de domaine">
      <div className="space-y-4 px-4 py-4 sm:px-6">
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
          <div className="min-w-0 rounded-xl bg-codialis-sand p-3">
            <div className="text-[11px] uppercase tracking-wide text-codialis-gray">Adresse des liens envoyés</div>
            <div className="mt-1 break-all font-mono text-sm font-semibold text-codialis-navy">{domaine.adresseActuelle}</div>
            <div className="mt-1 text-[11px] text-codialis-gray">
              {domaine.applique
                ? `Domaine personnalisé appliqué le ${dateReunion(domaine.applique.appliqueLe)}${
                    domaine.applique.appliquePar ? ` par ${domaine.applique.appliquePar}` : ''
                  }. L’adresse d’origine (${domaine.adresseParDefaut}) reste ouverte.`
                : 'Adresse d’origine de la plateforme.'}
            </div>
            {domaine.applique && (
              <button
                type="button"
                className={`${CLASSE_BOUTON_SECONDAIRE} mt-3`}
                disabled={enCours}
                onClick={() => setConfirmerRetrait(true)}
              >
                Revenir à l’adresse d’origine
              </button>
            )}
          </div>

          <div className="min-w-0 rounded-xl border border-gray-100 p-3 text-xs text-codialis-gray">
            <div className="font-semibold text-codialis-navy">Raccorder un domaine</div>
            <ol className="mt-1 list-decimal space-y-1 pl-4">
              <li>
                Chez le registraire du domaine, créer un enregistrement CNAME vers{' '}
                <span className="break-all font-mono text-codialis-navy">{domaine.cible}</span>.
              </li>
              <li>Demander à Codialis de déclarer le domaine au serveur web (certificat https compris).</li>
              <li>Vérifier ici, puis appliquer : les liens envoyés partiront de ce domaine.</li>
            </ol>
            <p className="mt-2">
              Les personnes connectées sur l’ancienne adresse s’y reconnecteront en suivant un nouveau lien : la session
              tient à l’adresse.
            </p>
          </div>
        </div>

        <form onSubmit={lancerVerification} className="flex flex-col gap-2 md:flex-row md:items-end">
          <div className="min-w-0 flex-1">
            <label htmlFor="marque-domaine" className="text-xs font-semibold text-codialis-navy">
              Domaine personnalisé
            </label>
            <input
              id="marque-domaine"
              type="text"
              inputMode="url"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="formation.topformation.re"
              value={saisie}
              onChange={(evenement) => {
                setErreur(null);
                setSaisie(evenement.target.value);
              }}
              className="mt-1 min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="submit" className={CLASSE_BOUTON_SECONDAIRE} disabled={enCours || !saisie.trim()}>
              {verifier.isPending ? 'Vérification…' : 'Vérifier'}
            </button>
            <button
              type="button"
              className={CLASSE_BOUTON_ENREGISTRER}
              disabled={
                enCours ||
                verification?.etat !== 'RACCORDE' ||
                dejaApplique ||
                verification.domaine !== saisie.trim().toLowerCase()
              }
              onClick={lancerApplication}
            >
              {appliquer.isPending ? 'Application…' : 'Appliquer aux liens envoyés'}
            </button>
          </div>
        </form>

        {verification && (
          <div className="flex flex-col gap-1 rounded-xl border border-gray-100 p-3 sm:flex-row sm:items-start sm:gap-3">
            <Pastille texte={verification.libelle} ton={TONS_ETAT[verification.etat]} />
            <div className="min-w-0 text-xs text-codialis-navy">
              <div className="break-words">{verification.detail}</div>
              <div className="mt-0.5 text-[11px] text-codialis-gray">
                {verification.domaine} · vérifié le {dateReunion(verification.verifieLe)}
                {dejaApplique ? ' · appliqué' : ''}
              </div>
            </div>
          </div>
        )}

        {erreur && <MessageErreur message={erreur} />}
      </div>

      <Modale
        titre="Revenir à l’adresse d’origine ?"
        ouverte={confirmerRetrait}
        onFermer={() => setConfirmerRetrait(false)}
        largeur="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-codialis-navy">
            Les liens envoyés repartiront de {domaine.adresseParDefaut}. Ceux déjà envoyés sur{' '}
            {domaine.applique?.adresse} restent valables tant que le domaine est raccordé.
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className={CLASSE_BOUTON_SECONDAIRE} onClick={() => setConfirmerRetrait(false)}>
              Garder le domaine
            </button>
            <button
              type="button"
              className={CLASSE_BOUTON_ENREGISTRER}
              disabled={retirer.isPending}
              onClick={() =>
                retirer.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmerRetrait(false);
                    toast('Les liens envoyés repartent de l’adresse d’origine');
                  },
                  onError: (refus) => {
                    setConfirmerRetrait(false);
                    setErreur(messageErreur(refus, 'Le domaine n’a pas pu être retiré.'));
                  },
                })
              }
            >
              {retirer.isPending ? 'Retrait…' : 'Revenir à l’adresse d’origine'}
            </button>
          </div>
        </div>
      </Modale>
    </Carte>
  );
}
